import type { ButtonHTMLAttributes, JSX } from 'react'
import { useSupportReadOnly } from '@/auth/support'

export type ButtonTone = 'primary' | 'secondary' | 'ghost' | 'quiet' | 'danger'

/**
 * A button that can be busy.
 *
 * `data-loading` is what the Atlas layer keys its spinner off, and it keeps the
 * button's width so the layout never jumps mid-submit (S-D1). The pointer is
 * disabled by CSS, but `disabled` is set as well: CSS alone still lets the
 * Enter key fire a focused button, which is how a double-submit happens on a
 * keyboard.
 *
 * The busy label is read out rather than left to the spinner, because a spinner
 * is invisible to a screen reader.
 */
export function Button({
  tone = 'ghost',
  size,
  block,
  busy = false,
  busyLabel = 'Working',
  unavailable = false,
  writes = false,
  children,
  ...rest
}: {
  /**
   * The visual weight. **Defaults to `ghost`, and omitting it is how a primary
   * action goes quiet without anyone noticing.**
   *
   * Recorded here because it has now cost four rows across three screens —
   * the inbox's Send in both its states (OUTR-68, OUTR-110), Cannibalization's
   * decision (SEO-UI-55) and RunTriggers' Save (SEO-UI-108). Each was a
   * `<Button>` in a card footer where the design draws the primary, rendering
   * as the quietest control in the system because nobody typed a word.
   *
   * The durable fix is to make this required, and it is not made here: `tone`
   * is omitted at most `<Button>` call sites in both workspaces, and changing
   * the appearance of screens that belong to later parity sessions — before
   * those sessions have compared them to their own mockups — would be deciding
   * their rows for them. **State it at every call site**; that is what makes
   * the eventual change a type error rather than a redesign.
   */
  tone?: ButtonTone
  size?: 'sm' | 'lg'
  block?: boolean
  busy?: boolean
  busyLabel?: string
  /**
   * Unavailable, and still reachable by keyboard (ONB-44).
   *
   * `disabled` takes a control out of the tab order entirely, so a screen-reader
   * user arrives at a card with no way to find the button at all — they cannot
   * hear that the primary action exists, let alone that it is waiting on the
   * checkbox above it. The Atlas layer's own pattern is `aria-disabled`
   * (`onboarding.html:285`): announced, focusable, inert. Visually identical,
   * because `components.css:98` keys off both.
   *
   * **Opt-in rather than the default**, for the reason `InlineError.takesFocus`
   * is: `disabled` is right for the ~89 call sites where the control is
   * unavailable because a request is mid-flight or a permission is absent, and
   * there is nothing at those to go and fix. This is for the case S-D2 draws —
   * a primary gated on something the person can act on, on this screen, now.
   *
   * `busy` still wins and still sets `disabled`: a submit in flight must not be
   * re-enterable by the Enter key, which is the double-submit this component
   * already guards.
   */
  unavailable?: boolean
  /**
   * This control **writes tenant data**, so it is unavailable during a read-only
   * support session (SUP-40, SF-0's NC-12).
   *
   * Screen 08 draws approve, publish and settings controls disabled inside a
   * read-only impersonation, so the staff member sees the boundary before
   * pressing rather than after. Marking a button `writes` renders it exactly as
   * `unavailable` does — announced, focusable, inert — with a `title` naming the
   * grant as the reason.
   *
   * **Opt-in, and it has to be**, for `unavailable`'s reason and one more. Most
   * buttons in this app are navigation, cancels, filters and disclosure toggles;
   * disabling those would leave a staff member unable to *look* at the account
   * they were given read access to, which is the one thing a read-only grant is
   * for. Only the presses that change the customer's data carry it.
   *
   * **It is not the enforcement.** `app/staff/policy.refuse` refuses every unsafe
   * method on a read-only grant before any route runs; this is the interface
   * describing that rule. `useSupportReadOnly`'s docstring is the long form.
   *
   * That distinction is load-bearing on a `type="submit"`: `aria-disabled` leaves
   * the control focusable, so **Enter pressed in a field of the same form still
   * submits it** — the browser never routes that through this button's `onClick`,
   * where the refusal lives. The request is then refused by the server with the
   * message that names the remedy, which is the correct outcome and is why this
   * is documented rather than closed by setting `disabled`: a real `disabled`
   * button takes no mouse events, so the `title` explaining *why* it is
   * unavailable would never be shown, and the control would vanish from the tab
   * order for the screen-reader user who most needs the explanation.
   */
  writes?: boolean
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>): JSX.Element {
  const supportReadOnly = useSupportReadOnly()
  // A `writes` button in a read-only support session is unavailable whatever the
  // screen asked for, and the screen's own `disabled` still applies on top: a
  // control that is both mid-flight and blocked is still mid-flight.
  const blocked = writes && supportReadOnly
  const isDisabled = rest.disabled === true || blocked
  // `blocked` uses the focusable treatment even where the call site did not ask
  // for it: a staff member navigating by keyboard has to be able to *find* the
  // control to learn it is blocked, which is the whole point of showing it
  // disabled rather than hiding it.
  const inert = unavailable || blocked
  const classes = [
    'ar-btn',
    `ar-btn--${tone}`,
    size ? `ar-btn--${size}` : null,
    block ? 'ar-btn--block' : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      {...rest}
      className={classes}
      data-loading={busy ? 'true' : undefined}
      disabled={busy || (isDisabled && !inert)}
      aria-disabled={!busy && isDisabled && inert ? true : undefined}
      title={blocked ? READ_ONLY_TITLE : rest.title}
      onClick={(event) => {
        // The whole cost of staying focusable: the browser will still deliver a
        // click, so the refusal has to be made here. `preventDefault` as well as
        // the early return, because a `type="submit"` in this state would
        // otherwise submit the form around it.
        if (isDisabled && inert) {
          event.preventDefault()
          return
        }
        rest.onClick?.(event)
      }}
    >
      {children}
      {busy ? <span className="ar-visually-hidden">{busyLabel}</span> : null}
    </button>
  )
}

/**
 * Why a `writes` button is inert, for the hover and for the accessible name.
 *
 * It names the *grant* rather than the person's permissions, because during
 * impersonation the two are different things and only one of them is the reason:
 * a staff member reading "you do not have permission" would go and look at the
 * customer's role.
 */
const READ_ONLY_TITLE =
  'Disabled during a read-only support session. Write access needs a second staff approval.'
